/**
 * The "nothing here" line every table, list and panel needs.
 *
 * The prototype wrote these inline in a dozen places with three recurring shapes,
 * reproduced exactly:
 *   · default  — 30 px padding, centred, 13 px muted ("No teammates match these
 *                filters. Try widening the search.", line 1188/1219);
 *   · compact  — 26 px padding, 12.5 px, for panels ("Nothing in the next 30 days.",
 *                line 304; "Nothing new.", line 1954);
 *   · inline   — left-aligned row with a leading icon ("No notes visible to you.",
 *                line 1362).
 */
import type { LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import "./ui.css";

export type EmptyStateVariant = "default" | "compact" | "inline";

export interface EmptyStateProps {
  /** The sentence itself — write it as guidance, not as an error. */
  children: ReactNode;
  variant?: EmptyStateVariant;
  icon?: LucideIcon;
  /** Optional bolder first line above the message. */
  title?: ReactNode;
  /** A single recovery action, e.g. "Clear filters". */
  action?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function EmptyState({
  children,
  variant = "default",
  icon: Icon,
  title,
  action,
  className,
  style,
}: EmptyStateProps) {
  const inline = variant === "inline";

  return (
    <div
      className={[
        "ui-empty",
        variant === "compact" ? "ui-empty--compact" : null,
        inline ? "ui-empty--inline" : null,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
    >
      {Icon ? (
        inline ? (
          <Icon size={12} aria-hidden="true" />
        ) : (
          <div className="ui-empty__icon">
            <Icon size={20} aria-hidden="true" />
          </div>
        )
      ) : null}
      {title ? <div className="ui-empty__title">{title}</div> : null}
      <div>{children}</div>
      {action ? <div className="ui-empty__action">{action}</div> : null}
    </div>
  );
}

export default EmptyState;
